import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnTabHeader from "@shared/components/SnTabHeader";
import SnTab from "@shared/components/SnTab";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showResizablePopupModal } from "@widgets/CommonModal";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import SN_PRD_BAS_REG_001_CHEM_POP_001 from "@menu/XTC/SN_PRD_BAS_REG_001_CHEM/SN_PRD_BAS_REG_001_CHEM_POP_001";
import SN_PRD_BAS_REG_001_CHEM_POP_002 from "@menu/XTC/SN_PRD_BAS_REG_001_CHEM/SN_PRD_BAS_REG_001_CHEM_POP_002";
import { RowNode } from "ag-grid-community";
import ExcelUpload from "./ExcelUpload";
import { global, globalMenu } from "@shared/libs/Global";
import { showPopupModal } from "@widgets/CommonModal";

interface Props {
  Menuprops: menuInfo;
}

// 레시피등록
const SN_PRD_BAS_REG_001_CHEM = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const searchRef = useRef<any>(null);
  const { userInfo, config } = global(); // Login user 정보

  const sheetref1 = useRef<any>(null); // excel download 용
  const sheetref2 = useRef<any>(null); // excel download 용
  const sheetref3 = useRef<any>(null); // excel download 용
  const sheetref4 = useRef<any>(null); // excel download 용
  const sheetref5 = useRef<any>(null); // excel download 용
  const sheetref6 = useRef<any>(null); // excel download 용
  const sheetref7 = useRef<any>(null); // excel download 용
  const sheetref8 = useRef<any>(null); // excel download 용
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    sheet1: [],
    sheet2: [],
    sheet3: [],
    sheet4: [],
    sheet5: [],
    sheet6: [],
    sheet7: [],
    sheet8: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    paramInfo: [],
    sheet1: [],
    sheet2: [],
    sheet3: [],
    sheet4: [],
    sheet5: [],
    sheet6: [],
    sheet7: [],
    sheet8: [],
  });

  const lastIndex = useRef(0);
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          sheet1: rtn.grid.sheet1,
          sheet2: rtn.grid.sheet2,
          sheet3: rtn.grid.sheet3,
          sheet4: rtn.grid.sheet4,
          sheet5: rtn.grid.sheet5,
          sheet6: rtn.grid.sheet6,
          sheet7: rtn.grid.sheet7,
          sheet8: rtn.grid.sheet8,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelUpload,
    onExcelDownLoad,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }

    const getSearchData = searchRef.current.getFormdata();
    if (
      getSearchData.cd_item === null ||
      getSearchData.cd_item === undefined ||
      getSearchData.cd_item === ""
    ) {
      await showDialog("alter", "품목은 필수조건입니다.");
      return false;
    }

    const requiredValuesCheckSearch = searchRef.current.requiredValuesCheck();
    if (!requiredValuesCheckSearch) return false;

    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_prd_bas_reg_001_chem_01",
        params: searchDataToUse,
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);

      // grid2 데이터 로드 시 원본 복사본(__originalData) 생성
      const grid2WithOrigin = (data[1] || []).map((row: any) => ({
        ...row,
        __originalData: { ...row },
      }));

      const focusedNode = gridRefs.current["grid1"].current.focusedRowNode();
      lastIndex.current = focusedNode ? focusedNode.rowIndex : 0; // 포커스가 없으면 0번 인덱스

      setDataSources({
        grid1: data[0],
        grid2: grid2WithOrigin,
        grid3: data[2] || [],
        paramInfo: data[3] || [],
        sheet1: [],
        sheet2: [],
        sheet3: [],
        sheet4: [],
        sheet5: [],
        sheet6: [],
        sheet7: [],
        sheet8: [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    const getSearchData = searchRef.current.getFormdata();
    if (
      getSearchData.cd_item === null ||
      getSearchData.cd_item === undefined ||
      getSearchData.cd_item === ""
    ) {
      showDialog("alter", "품목은 필수조건입니다.");
      return false;
    }

    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      // gridRefs.current["grid1"].current.resetUpdate();
      // gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //region onExcelUpload
  const onExcelUpload = async () => {
    try {
      const searchData = searchRef.current.getFormdata();
      const component = (
        <ExcelUpload
          Menuprops={Menuprops} // 메뉴 정보 전달
          PkFields={["cd_biz", "cd_item", "no_line"]} // PK 필드 목록 전달
          tableName="prd_recipe" // 저장할 테이블 이름
          controllerName="SN_PRD_BAS_REG_001_CHEM_SAVE_01" // 저장할 컨트롤러 이름
          pkCheckCode=""
          defaultValue={{ cd_biz: searchData.cd_biz }}
        />
      );

      const result = await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });

      if (result) {
        console.log("엑셀 업로드 완료:", result);
      } else {
        console.log("엑셀 업로드 취소됨");
      }
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  //#region onExcelDownLoad
  const onExcelDownLoad = async () => {
    try {
      const columnKeys1 = sheetref1.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());
      const columnKeys2 = sheetref2.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());
      const columnKeys3 = sheetref3.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());
      const columnKeys4 = sheetref4.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());
      const columnKeys5 = sheetref5.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());
      const columnKeys6 = sheetref6.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());
      const columnKeys7 = sheetref7.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());

      const columnKeys8 = sheetref8.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());

      const params1 = {
        columnKeys: columnKeys1,
        sheetName: "작업정보",
        skipPinnedBottom: true,
      };

      const params2 = {
        columnKeys: columnKeys2,
        sheetName: "교반기",
        skipPinnedBottom: true,
      };

      const params3 = {
        columnKeys: columnKeys3,
        sheetName: "저울",
        skipPinnedBottom: true,
      };

      const params4 = {
        columnKeys: columnKeys4,
        sheetName: "밀링기",
        skipPinnedBottom: true,
      };

      const params5 = {
        columnKeys: columnKeys5,
        sheetName: "검사기",
        skipPinnedBottom: true,
      };

      const params6 = {
        columnKeys: columnKeys6,
        sheetName: "진공탈포기",
        skipPinnedBottom: true,
      };

      const params7 = {
        columnKeys: columnKeys7,
        sheetName: "수작업",
        skipPinnedBottom: true,
      };

      const params8 = {
        columnKeys: columnKeys8,
        sheetName: "여과기",
        skipPinnedBottom: true,
      };

      sheetref1.current?.exportMultipleSheetsAsExcel({
        data: [
          sheetref1.current?.getSheetDataForExcel(params1),
          sheetref2.current?.getSheetDataForExcel(params2),
          sheetref3.current?.getSheetDataForExcel(params3),
          sheetref4.current?.getSheetDataForExcel(params4),
          sheetref5.current?.getSheetDataForExcel(params5),
          sheetref6.current?.getSheetDataForExcel(params6),
          sheetref7.current?.getSheetDataForExcel(params7),
          sheetref8.current?.getSheetDataForExcel(params8),
        ],
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) {
      return;
    }
    gridRefs.current["grid2"].current?.deleteSelectedRows();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const recipegroupChanges = gridRefs.current["grid1"].current.getChange();
      const recipeChanges = gridRefs.current["grid2"].current.getChange();

      if (
        recipegroupChanges.modifiedRowcount === 0 &&
        recipeChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 신규 행 체크: 설비의 yn_check='Y' 파라미터 중 한 건이라도 입력됐는지 확인(pivot)
      const isEmptyValue = (v: any) =>
        v === undefined || v === null || String(v).trim() === "";
      // grid1의 cd_group -> nm_group 매핑(메시지 표시용)
      const groupNameMap = new Map<string, string>();
      gridRefs.current["grid1"].current?.forEachNode((node: RowNode) => {
        if (node.data?.cd_group != null) {
          groupNameMap.set(String(node.data.cd_group), node.data.nm_group);
        }
      });
      const notEnteredGroups = Array.from(
        new Set(
          (recipeChanges.insert ?? [])
            .filter((row: any) => {
              const checkParams = dataSources.paramInfo.filter(
                (p: any) =>
                  p.cd_equip === row.cd_grp_equip && p.yn_check === "Y"
              );
              if (checkParams.length === 0) return false;
              return checkParams.every((p: any) =>
                isEmptyValue(row[String(p.no_line_param)])
              );
            })
            .map(
              (row: any) =>
                groupNameMap.get(String(row.cd_group)) ?? row.cd_group
            )
        )
      );
      if (notEnteredGroups.length > 0) {
        await showDialog(
          "alter",
          `입력되지 않은 항목이 있습니다. [${notEnteredGroups.join(", ")}]`
        );
        return false;
      }

      const allColumns = gridRefs.current["grid2"].current.getAllGridColumns();
      const paramColumnIds = allColumns
        .map((col: any) => col.getColId())
        .filter((id: string) => !isNaN(Number(id))); // 필드명이 숫자인 것만 추출

      const valueChanges = {
        insert: [] as any[],
        update: [] as any[],
        delete: [] as any[],
        modifiedRowcount: 0,
      };

      const unpivotRow = (
        row: any,
        targetArray: any[],
        mode: "insert" | "update" | "delete"
      ) => {
        paramColumnIds.forEach((paramId: string) => {
          const newValue = row[paramId];
          const originalValue = row.__originalData
            ? row.__originalData[paramId]
            : undefined;

          const hasValue =
            newValue !== undefined &&
            newValue !== null &&
            String(newValue).trim() !== "";
          const hadOriginalValue =
            originalValue !== undefined &&
            originalValue !== null &&
            String(originalValue).trim() !== "";

          const item = {
            cd_firm: row.cd_firm,
            cd_biz: row.cd_biz,
            cd_item: row.cd_item,
            no_line_recipe: row.no_line,
            no_line_param: Number(paramId),
            dc_value: String(newValue || ""),
          };

          if (mode === "update") {
            if (hasValue && !hadOriginalValue) {
              valueChanges.insert.push(item); // 신규 입력
            } else if (hasValue && hadOriginalValue) {
              if (String(newValue) !== String(originalValue)) {
                valueChanges.update.push(item); // 기존 값 수정
              }
            } else if (!hasValue && hadOriginalValue) {
              valueChanges.delete.push(item); // 기존 값 삭제
            }
          } else {
            if (hasValue) targetArray.push(item);
          }
        });
      };

      recipeChanges.delete.forEach((row: any) =>
        unpivotRow(row, valueChanges.delete, "delete")
      );
      recipeChanges.insert.forEach((row: any) =>
        unpivotRow(row, valueChanges.insert, "insert")
      );
      recipeChanges.update.forEach((row: any) =>
        unpivotRow(row, valueChanges.update, "update")
      );

      // 전체 변경 건수 계산
      valueChanges.modifiedRowcount =
        valueChanges.insert.length +
        valueChanges.update.length +
        valueChanges.delete.length;

      // 요청 데이터 구성
      const SaveData = {
        recipegroup: recipegroupChanges,
        recipe: recipeChanges,
        value: valueChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PRD_BAS_REG_001_CHEM_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 재조회(pivot이 update로 들어갈지 insert로 들어갈지 구분 할 방법이 없어서)
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      onSearch(null);
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region onBeforeAddRow1
  const onBeforeAddRow1 = (): any => {
    try {
      const searchInfo = searchRef.current.getFormdata();
      const maxNoSeq =
        gridRefs.current["grid1"].current.getMaxValue("no_seq_group");
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_item: searchInfo?.cd_item,
        no_seq_group: maxNoSeq + 1,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };
  //#endregion

  //#region onCellValueChangedH
  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;

    switch (colId) {
      case "cd_grp_equip":
        gridRefs.current["grid2"].current?.forEachFilteredNode(
          (node: RowNode) => {
            node.setDataValue("cd_grp_equip", rowData.cd_grp_equip);
          }
        );
        const virtual = updateColumnsByFilter(
          columns.grid2,
          rowData.cd_grp_equip
        );
        gridRefs.current["grid2"].current.changeColumnReorderConfigs(virtual);
        break;
    }
  };
  //#endregion

  //#region onBtnsClick
  const onBtnsClick = async (id: string) => {
    switch (id) {
      case "btn_POP1":
        // searchPanel에서 선택된 값 전달
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
        };
        const component = (
          <SN_PRD_BAS_REG_001_CHEM_POP_001 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "파라미터등록",
          children: component,
          width: "1500",
          height: "800px",
          okayBtn: false,
        });
        break;
      case "btn_SOP": {
        const focusedNodeH =
          gridRefs.current["grid1"].current?.focusedRowNode();
        if (!focusedNodeH) {
          await showDialog("alter", "작업그룹을 선택해주세요.");
          return;
        }
        const sopComponent = (
          <SN_PRD_BAS_REG_001_CHEM_POP_002
            selectedData={{ cd_group: focusedNodeH.data?.cd_group }}
          />
        );
        await showResizablePopupModal({
          title: "SOP 파일관리",
          children: sopComponent,
          width: "1000px",
          height: "600px",
          okayBtn: false,
        });
        break;
      }
    }
  };
  //#endregion

  //#region Grid1BtnClick
  const onGrid1BtnsClick = async (id: string) => {
    const getSearchData = searchRef.current.getFormdata();
    if (
      getSearchData.cd_item === null ||
      getSearchData.cd_item === undefined ||
      getSearchData.cd_item === ""
    ) {
      return false;
    }

    switch (id) {
      case "btnUp":
        break;
      case "btnDown":
        break;
    }
  };
  //#endregion

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const virtual = updateColumnsByFilter(
        columns.grid2,
        selectedRow.cd_grp_equip
      );
      gridRefs.current["grid2"].current.changeColumnReorderConfigs(virtual);
    } else {
      return;
    }
  };

  //#region onBeforeAddRow2
  const onBeforeAddRow2 = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }
      const maxNoLine =
        gridRefs.current["grid2"].current.getNonFilterMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        cd_item: focusedNodeH.data?.cd_item,
        cd_group: focusedNodeH.data?.cd_group,
        cd_grp_equip: focusedNodeH.data?.cd_grp_equip,
        no_line: maxNoLine + 1,
        yn_end_group: "N",
        // no_seq : 1,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };
  //#endregion

  //#region grid2Btns
  const onGrid2BtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
      case "btnUp":
        break;
    }
  };
  //#endregion

  //#region updateColumnsByFilter
  const updateColumnsByFilter = (gridColumns: any[], equip: any) => {
    const filterArray = dataSources.paramInfo.filter(
      (item: any) => item.cd_equip === equip
    );

    const lastColumnIds = ["dc_rmk"]; // 맨 뒤로 보낸 컬럼

    const normalColumns: any[] = []; // dummy가 아닌 일반 컬럼들
    const sortedColumns: any[] = []; // no_seq로 정렬할 컬럼
    const finalColumns: any[] = []; // 무조건 맨 뒤로 갈 컬럼

    gridColumns.forEach((row: any) => {
      const matchedItem = filterArray.find(
        (filterItem: any) => filterItem.no_line_param == row.cd_column
      );

      // dummy가 아닌 값들은 원본 순서대로 유지하기 위해 그대로 push
      if (lastColumnIds.includes(row.cd_column)) {
        finalColumns.push({
          cd_column: row.cd_column,
          fg_visible: row.fg_visible,
        });
      } else if (row.cd_clas !== "dummy") {
        normalColumns.push({
          cd_column: row.cd_column,
          fg_visible: row.fg_visible,
        });
      }
      // dummy인 애들은 매칭 여부에 따라 데이터를 가공해서 모음
      else {
        if (matchedItem) {
          sortedColumns.push({
            cd_column: row.cd_column,
            nm_column: matchedItem.nm_param,
            fg_visible: "Y",
            no_seq: matchedItem.no_seq, // 정렬 기준
          });
        } else {
          sortedColumns.push({
            cd_column: row.cd_column,
            nm_column: row.cd_column,
            fg_visible: "N",
            no_seq: null, // 미매칭(미사용 파라미터 등) → 정렬 시 맨 뒤
          });
        }
      }
    });

    // no_seq가 없거나 숫자가 아닌 컬럼(숨김)은 맨 뒤로, 그 외는 no_seq 오름차순.
    // no_seq가 같으면 cd_column 숫자 순으로 고정 → 비교 결과 일관성 보장(NaN 방지)
    const toSeq = (v: any) => {
      const n = Number(v);
      return v === null || v === undefined || v === "" || Number.isNaN(n)
        ? Number.MAX_SAFE_INTEGER
        : n;
    };
    sortedColumns.sort((a, b) => {
      const diff = toSeq(a.no_seq) - toSeq(b.no_seq);
      if (diff !== 0) return diff;
      return Number(a.cd_column) - Number(b.cd_column) || 0;
    });

    return [...normalColumns, ...sortedColumns, ...finalColumns];
  };

  const onRowDataReady = () => {
    if (lastIndex.current) {
      gridRefs.current["grid1"].current.selectAndFocusRow(lastIndex.current);
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onBeforeAddRow: onBeforeAddRow1,
          onCellValueChanged: onCellValueChangedH,
          onRowDataReady: onRowDataReady,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onBeforeAddRow: onBeforeAddRow2,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
      sheet2: {
        columns: columns.sheet2,
        dataSource: dataSources.sheet2,
      },
      sheet3: {
        columns: columns.sheet3,
        dataSource: dataSources.sheet3,
      },
      sheet4: {
        columns: columns.sheet4,
        dataSource: dataSources.sheet4,
      },
      sheet5: {
        columns: columns.sheet5,
        dataSource: dataSources.sheet5,
      },
      sheet6: {
        columns: columns.sheet6,
        dataSource: dataSources.sheet6,
      },
      sheet7: {
        columns: columns.sheet7,
        dataSource: dataSources.sheet7,
      },
      sheet8: {
        columns: columns.sheet8,
        dataSource: dataSources.sheet8,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
          }}
        >
          <SnButton
            text="파라미터등록"
            onClick={() => {
              onBtnsClick("btn_POP1");
            }}
          />
        </div>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="35%">
          <SnTab
            titles={["작업그룹", "BOM"]}
            width={"100%"}
            header={[
              <SnTabHeader
                body={
                  config?.MAS8025?.dc_rmk === "EICT"
                    ? [
                        <SnButton
                          text={"SOP"}
                          onClick={() => {
                            onBtnsClick("btn_SOP");
                          }}
                        />,
                      ]
                    : undefined
                }
              />,
            ]}
            body={[
              <SnGrid
                ref={gridRefs.current["grid1"]} // Grid Name
                columnConfig={grids.grid1.columns} // 컬럼 설정 전달
                rowData={grids.grid1.dataSource} // Datasource
                etcProps={{
                  rowSelection: "single",
                  rowDragOrderKey: "no_seq_group", // No. 칸을 끌어 순서 변경 → 순번 재부여
                }}
                events={grids.grid1.events}
                filter={{
                  refs: [gridRefs.current["grid2"]],
                  columns: ["cd_group"],
                }}
              />,

              <SnGrid
                ref={gridRefs.current["grid3"]} // Grid Name
                columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                rowData={grids.grid3.dataSource} // Datasource
                etcProps={{ rowSelection: "single" }}
              />,
              <div style={{ display: "none" }}>
                <SnGrid
                  ref={sheetref1} // Grid Name
                  columnConfig={grids.sheet1.columns} // 컬럼 설정 전달
                  rowData={grids.sheet1.dataSource} // Datasource
                />
              </div>,
              <div style={{ display: "none" }}>
                <SnGrid
                  ref={sheetref2} // Grid Name
                  columnConfig={grids.sheet2.columns} // 컬럼 설정 전달
                  rowData={grids.sheet2.dataSource} // Datasource
                />
              </div>,
              <div style={{ display: "none" }}>
                <SnGrid
                  ref={sheetref3} // Grid Name
                  columnConfig={grids.sheet3.columns} // 컬럼 설정 전달
                  rowData={grids.sheet3.dataSource} // Datasource
                />
              </div>,
              <div style={{ display: "none" }}>
                <SnGrid
                  ref={sheetref4} // Grid Name
                  columnConfig={grids.sheet4.columns} // 컬럼 설정 전달
                  rowData={grids.sheet4.dataSource} // Datasource
                />
              </div>,
              <div style={{ display: "none" }}>
                <SnGrid
                  ref={sheetref5} // Grid Name
                  columnConfig={grids.sheet5.columns} // 컬럼 설정 전달
                  rowData={grids.sheet5.dataSource} // Datasource
                />
              </div>,
              <div style={{ display: "none" }}>
                <SnGrid
                  ref={sheetref6} // Grid Name
                  columnConfig={grids.sheet6.columns} // 컬럼 설정 전달
                  rowData={grids.sheet6.dataSource} // Datasource
                />
              </div>,
              <div style={{ display: "none" }}>
                <SnGrid
                  ref={sheetref7} // Grid Name
                  columnConfig={grids.sheet7.columns} // 컬럼 설정 전달
                  rowData={grids.sheet7.dataSource} // Datasource
                />
              </div>,
              <div style={{ display: "none" }}>
                <SnGrid
                  ref={sheetref8} // Grid Name
                  columnConfig={grids.sheet8.columns} // 컬럼 설정 전달
                  rowData={grids.sheet8.dataSource} // Datasource
                />
              </div>,
            ]}
          />
        </SnResizeBar>
        <SnGrid
          ref={gridRefs.current["grid2"]} // Grid Name
          columnConfig={grids.grid2.columns} // 컬럼 설정 전달
          rowData={grids.grid2.dataSource} // Datasource
          etcProps={{ rowSelection: "single", title: "레시피" }}
          events={grids.grid2.events}
          // header={
          //   <div>
          //     <SnButton
          //       text={"▲"}
          //       onClick={() => {
          //         onGrid2BtnsClick("btnUp");}}
          //     />
          //     <SnButton
          //       text={"▼"}
          //       onClick={() => {
          //         onGrid2BtnsClick("btnDown");}}
          //     />
          //   </div>}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_PRD_BAS_REG_001_CHEM;
